import type { CartNotification, CartNotificationQtyUpdate } from '@libs/common-types';
import {
    isCartNotificationCouponEffect,
    isCartNotificationDiscontinuedItemsRemoved,
    isCartNotificationTreatPointsRemoved,
} from '@libs/common-types';
import { CartNotificationAction } from '@libs/core';

import { isScheduledDeliveryDateUpdatedNotification } from './cartValidationNotificationUtils';

export const isMaxQtyLineItemNotification = (
    notification: CartNotification
): notification is CartNotificationQtyUpdate =>
    notification.action === CartNotificationAction.LINE_ITEM_QTY_UPDATE && 'maxOrderQty' in notification;

const isPreservedClientNotification = (notification: CartNotification): boolean =>
    isCartNotificationDiscontinuedItemsRemoved(notification) ||
    isScheduledDeliveryDateUpdatedNotification(notification) ||
    isCartNotificationTreatPointsRemoved(notification) ||
    (isCartNotificationCouponEffect(notification) && notification.type === 'error');

const getCouponEffectKey = (notification: CartNotification): string | null => {
    if (!isCartNotificationCouponEffect(notification)) {
        return null;
    }

    return `${notification.couponCode.toUpperCase()}:${notification.code}:${notification.type}`;
};

export const mergeCartNotificationsPreservingDiscontinuedRemoved = (
    prev: CartNotification[] | undefined | null,
    incoming: CartNotification[] | undefined | null
): CartNotification[] => {
    const incomingList = incoming ?? [];
    const prevList = prev ?? [];
    const preserved = prevList.filter(isPreservedClientNotification);
    const incomingHasDiscontinued = incomingList.some(isCartNotificationDiscontinuedItemsRemoved);
    const incomingHasScheduledDeliveryDateUpdated = incomingList.some(isScheduledDeliveryDateUpdatedNotification);
    const incomingHasTreatPointsRemoved = incomingList.some(isCartNotificationTreatPointsRemoved);
    const incomingKeys = new Set(incomingList.map(getCouponEffectKey).filter((key): key is string => key != null));
    const preservedNotAlreadyIncoming = preserved.filter(notification => {
        if (isCartNotificationDiscontinuedItemsRemoved(notification)) {
            return !incomingHasDiscontinued;
        }

        if (isScheduledDeliveryDateUpdatedNotification(notification)) {
            return !incomingHasScheduledDeliveryDateUpdated;
        }

        if (isCartNotificationTreatPointsRemoved(notification)) {
            return !incomingHasTreatPointsRemoved;
        }

        const key = getCouponEffectKey(notification);

        return key == null || !incomingKeys.has(key);
    });

    return [...incomingList, ...preservedNotAlreadyIncoming];
};

/** Keeps validate-on-load banners when subscription refresh returns a plain cart snapshot without them. */
export const mergeCartNotificationsPreservingValidateOnLoad = (
    validateOnLoadNotifications: CartNotification[] | undefined | null,
    incoming: CartNotification[] | undefined | null
): CartNotification[] => {
    const merged = mergeCartNotificationsPreservingDiscontinuedRemoved(validateOnLoadNotifications, incoming);

    const maxQtyFromValidate = (validateOnLoadNotifications ?? []).filter(isMaxQtyLineItemNotification);
    if (maxQtyFromValidate.length === 0) {
        return merged;
    }

    const mergedMaxQtyLineItemIds = new Set(
        merged.filter(isMaxQtyLineItemNotification).map(notification => notification.lineItemId)
    );
    const additionalMaxQtyNotifications = maxQtyFromValidate.filter(
        notification => !mergedMaxQtyLineItemIds.has(notification.lineItemId)
    );

    return additionalMaxQtyNotifications.length > 0 ? [...merged, ...additionalMaxQtyNotifications] : merged;
};
